import { hasNonEmptyValue } from 'Lib/Any';
import { useEffect, useState } from 'react';
import { unstable_useBlocker, useNavigate } from 'react-router';
import { Location, unstable_BlockerFunction } from 'react-router-dom';

type UsePromptOnNavigateArgs = {
  /**
   * If true, user will be prompted when navigating away
   */
  when: boolean | unstable_BlockerFunction;
  /**
   * Defines the message the user will be shown in the confirmation dialog.
   */
  message: string;
};

/**
 * Causes the user to be prompted to confirm when navigating away (e.g. to warn about unsaved changes).
 *
 * This mimicks the functionality of react-router unstable_usePrompt,
 * which was added to replace component Prompt that we previously used.
 *
 * We have written our own version of this to resolve an issue where this throws an error in Firefox -
 * see https://github.com/remix-run/react-router/issues/10314
 *
 * This is fairly sketchy, but it seems to be doing what we need for Chrome, Firefox and Chrome in WebKitX.
 *
 * If you are reading this, check whether the latest version of react-router has stabilised this feature
 * so we can remove this hacky hook. In the meantime, DO NOT MODIFY THE BEHAVIOUR OF THIS FUNCTION
 * OTHER THAN TO FIX ISSUES WITH REACT-ROUTER'S IMPLEMENTATION
 */
export function usePromptOnNavigate({ when, message }: UsePromptOnNavigateArgs) {
  let blocker = unstable_useBlocker(when);

  const navigate = useNavigate();

  const [proceed, setProceed] = useState<boolean | undefined>(undefined);
  const [proceedTo, setProceedTo] = useState<Location | undefined>();

  useEffect(() => {
    if (blocker.state === 'blocked' && !when) {
      blocker.reset();
      setProceed(undefined);
    }
  }, [blocker, when]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      // Store location in state in case we need to navigate to the blocked location manually
      setProceedTo(blocker.location);
      // Unlike the regular implementation, we wrap window.confirm in a timeout
      // and handle this when it changes (using state)
      setTimeout(() => setProceed(window.confirm(message)), 0);
    }
  }, [blocker, message]);

  useEffect(() => {
    if (proceed) {
      // If blocker is still blocked, we proceed via the blocker
      if (blocker.state === 'blocked') {
        setTimeout(blocker.proceed, 0);
      }
      // If blocker has since become unblocked, we must navigate to the location directly
      else if (proceedTo) {
        navigate(proceedTo.pathname, hasNonEmptyValue(proceedTo.state) ? { state: proceedTo.state } : undefined);
      }
      setProceed(undefined);
    }
  }, [blocker, proceed, navigate, proceedTo]);
}
